import React, {JSX, useEffect} from 'react';
import {GestureHandlerRootView} from 'react-native-gesture-handler';
import {LogBox, Platform} from 'react-native';
import {useSelector, useDispatch} from 'react-redux';
import {RootState} from './redux/store';
import {logAppOpenWithFrequency} from './utils/helpers';
import {SafeAreaProvider} from 'react-native-safe-area-context';
import {setDark} from './screens/content/settings/reducer';
import {ResponsiveThemeProvider} from './theme/ResponsiveThemeProvider';
import documentTitleManager from './utils/documentTitle';

// Import components directly to avoid lazy loading issues initially
import AppNavigator from './router';
import GlobalToast from './connected-components/toast';
import GlobalLoading from './connected-components/loading';
import FiltersModal from './connected-components/filtersModal';
import DateRangePickerModal from './connected-components/dateRangePickerModal';
import ChooseSpecialtyModal from './connected-components/chooseSpecialtyModal';
import SpecialityModal from './connected-components/specialityModal';
import SettingsModal from './connected-components/settingsModal';
import GlobalUploadModal from './connected-components/uploadModal';
import CountryPickerModal from './connected-components/countryPickerModal';
import QuestionModal from './connected-components/questionModal';

/**
 * The root component of the application.
 *
 * @returns {JSX.Element} The rendered root component.
 */
const App = (): JSX.Element => {
  const dispatch = useDispatch();
  const {darkMode} = useSelector((state: RootState) => state?.settings) || {};

  useEffect(() => {
    // Only check local storage theme on web
    if (Platform.OS === 'web') {
      const storedTheme = localStorage.getItem('GENIO__DARK_MODE');
      const storedAppTheme = localStorage.getItem('app_theme');
      // Check both possible storage keys
      const isDarkMode = storedTheme === 'true' || storedAppTheme === 'dark';
      // If there's a stored theme and it differs from current Redux state
      if (isDarkMode !== darkMode) {
        dispatch(setDark({darkMode: isDarkMode}));
      }
    }
  }, []); // Empty dependency array ensures this runs only once on mount

  useEffect(() => {
    logAppOpenWithFrequency();
    LogBox.ignoreLogs([
      '(ADVICE) View #4577 of type RCTView has a shadow set but cannot calculate shadow efficiently',
      'Warning: React has detected a change in the order',
      'VirtualizedLists should never be nested inside plain ScrollViews',
    ]);
    
    // Initialize document title for web platform
    documentTitleManager.initialize();
  }, []);

  return (
    <SafeAreaProvider>
      <ResponsiveThemeProvider isDarkMode={darkMode}>
        <GestureHandlerRootView style={{flex: 1}}>
          <AppNavigator />
          <DateRangePickerModal />
          <FiltersModal />
          <QuestionModal />
          <GlobalToast />
          <ChooseSpecialtyModal />
          <GlobalUploadModal />
          <SpecialityModal />
          <SettingsModal />
          <GlobalLoading />
          <CountryPickerModal />
        </GestureHandlerRootView>
      </ResponsiveThemeProvider>
    </SafeAreaProvider>
  );
};

export default App;
